import React, { useState } from 'react';
import cx from 'classnames';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { useTranslation } from 'react-i18next';
import { useLayer } from 'react-laag';

import * as Icon from 'components/Icon';

import classes from './Filter.module.scss';

interface IProps {
  title: string;
  value?: string;
  onClear?: () => void;
  children: ({ open, close }: { open: () => void; close: () => void }) => React.ReactNode;
}

const Filter: React.FC<IProps> = ({ title, value, onClear, children }) => {
  const [isOpen, setOpen] = useState(false);
  const { t } = useTranslation();

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen,
    overflowContainer: window.matchMedia('(min-width: 576px)').matches,
    placement: 'bottom-center',
    auto: true,
    possiblePlacements: ['bottom-start', 'bottom-end', 'bottom-center'],
    snap: false,
    preferX: 'left',
    triggerOffset: 15,
    containerOffset: 15,
    container: 'content',
    onOutsideClick: () => setOpen(false),
    onDisappear: disappearType => {
      if (disappearType === 'full') {
        setOpen(false);
      }
    }
  });

  const onClearClick = (e) => {
    e.stopPropagation();
    onClear && onClear();
  };

  return (
    <>
      <div
        className={cx(classes.wrapper, value && classes[`wrapper--has-value`], isOpen && classes[`wrapper--opened`])}
        title={value ? `${title}: ${value}` : ''}
      >
        <div {...triggerProps} className={classes.content} onClick={() => setOpen(!isOpen)}>
          <div className={classes.title}>{title}:</div>
          <div className={classes.value}>{value || t('All')}</div>
        </div>
        {!!value && (
          <div className={classes.clear} onClick={onClearClick}>
            <Icon.System.CrossFilled/>
          </div>
        )}
      </div>
      <Transition in={isOpen} timeout={{ enter: 0, exit: 200 }} unmountOnExit mountOnEnter>
        {(state: TransitionStatus) => (
          <>
            {renderLayer(
              <div {...layerProps}>
                <div className={classes.dropdown} style={{ ...transition[state] }}>
                  {children({
                    open: () => setOpen(true),
                    close: () => setOpen(false)
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </Transition>
    </>
  );
};

const transition = {
  entering: { opacity: 0, visibility: 'hidden' },
  entered: { opacity: 1, visibility: 'visible', transition: 'all .2s ease-in-out' },
  exited: { opacity: 0, visibility: 'hidden' }
};

export default Filter;